import {
  WSAlert,
  WSButton,
  WSCard,
  WSDataItem,
  WSEmptyState,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { Batch, BatchStatus, BatchSummary } from "@wingspanhq/wingspan-api-sdk";
import pluralize from "pluralize";
import queryString from "qs";
import { useHistory } from "react-router-dom";

import { PAYEES_ROOT_PATH } from "../../../Payees/utils";
import { ImportBatchProgress } from "../../components/ImportBatchProgress";
import { ImportBatchUploadFailedAlert } from "../../components/ImportBatchUploadFailedAlert";
import { ImportBatchUploadProgress } from "../../components/ImportBatchUploadProgress";
import { ImportBatchUploadRejectionsAlert } from "../../components/ImportBatchUploadRejectionsAlert";
import { ImportBatchUploadUnuploadedRowsAlert } from "../../components/ImportBatchUploadUnuploadedRowsAlert";
import { useModalContractorColumnsGuide } from "../../components/ModalContractorColumnsGuide";
import { CONTRACTORS_SHEET_SLUG } from "../../constants";
import { useFailedBatchItems } from "../../hooks/useFailedBatchItems";
import { useFlatfileUploadJobStatus } from "../../hooks/useFlatfileUploadJobStatus";
import { useImportBatchStatus } from "../../hooks/useImportBatchStatus";
import { useImportBatchStatusActions } from "../../hooks/useImportBatchActions";
import { useSkippedRowsCount } from "../../hooks/useSkippedRowsCount";
import { selectorImportBatchUploadedFilename } from "../../selectors/selectorImportBatchUploadedFilename";
import { selectorIsImportBatchFileUploaded } from "../../selectors/selectorIsImportBatchFileUploaded";
import { selectorIsImportBatchFullyImported } from "../../selectors/selectorIsImportBatchFullyImported";
import { selectorIsImportBatchPartiallyImported } from "../../selectors/selectorIsImportBatchPartiallyImported";
import { selectorIsImportBatchUploadFinished } from "../../selectors/selectorIsImportBatchUploadFinished";
import { selectorIsImportBatchUploadInProgress } from "../../selectors/selectorIsImportBatchUploadInProgress";
import {
  CONTRACTOR_BATCH_DELETE_DESCRIPTION,
  CONTRACTOR_BATCH_DELETE_EMPTY_DESCRIPTION,
  CONTRACTOR_ENTITY_LABEL,
  CONTRACTOR_ENTITY_LABEL_PLURAL_LOWER
} from "./constants";

type Props = {
  batch: Batch;
  /** Resolved before the page renders, so the row counts are never unknown. */
  batchSummary: BatchSummary;
  downloadExampleCSV: () => void;
  downloadErrorsCSV: () => void;
  onDeleted: () => void;
};

export const ContractorBatchStatusWidget: React.FC<Props> = ({
  batch,
  batchSummary,
  downloadExampleCSV,
  downloadErrorsCSV,
  onDeleted
}) => {
  const batchId = batch.id as string;
  const history = useHistory();
  const skippedRowsCount = useSkippedRowsCount(batch, CONTRACTORS_SHEET_SLUG);
  const contractorsColumnsGuideModal = useModalContractorColumnsGuide();

  const progress = batchSummary.progress;
  const itemCount = progress?.total;
  const failedCount = progress?.failed;
  const isEmptyBatch = itemCount === 0;

  const { failedBatchItems } = useFailedBatchItems({ batch, failedCount });
  const { isFailed: isUploadFailed } = useFlatfileUploadJobStatus(batch);
  const { pill } = useImportBatchStatus(batchId, true);

  const { menuItems, buttons } = useImportBatchStatusActions({
    batch,
    isEmptyBatch,
    failedCount,
    entityLabel: CONTRACTOR_ENTITY_LABEL,
    entityLabelPlural: CONTRACTOR_ENTITY_LABEL_PLURAL_LOWER,
    deleteDescription: CONTRACTOR_BATCH_DELETE_DESCRIPTION,
    deleteEmptyBatchDescription: CONTRACTOR_BATCH_DELETE_EMPTY_DESCRIPTION,
    downloadExampleCSV,
    downloadErrorsCSV,
    onDeleted,
    hideButtonOn: false
  });

  const isBatchProcessing =
    batch.status === BatchStatus.PENDING ||
    batch.status === BatchStatus.PROCESSING;

  const goToContractors = () => {
    // Scalar, not v1's one-element array: the contractors list parses the query
    // string flat and maps the value straight onto `batchIds.eq`.
    history.push(
      `${PAYEES_ROOT_PATH}?${queryString.stringify({ bulkBatchId: batchId })}`
    );
  };

  const renderStats = (showSeeAll: boolean = true) => (
    <WSList gap="XS">
      <WSFlexBox.CenterY justify="space-between">
        <WSText.Paragraph weight="medium" color="gray600">
          Contractors
        </WSText.Paragraph>
        {showSeeAll && (
          <WSButton.Link rightIcon="chevron-right" onClick={goToContractors}>
            See all
          </WSButton.Link>
        )}
      </WSFlexBox.CenterY>
      <WSDataItem inline label="Total contractors" value={itemCount ?? "--"} />
    </WSList>
  );

  const renderErrorsFound = () => (
    <WSAlert icon="alert" theme="warning" title="Errors found">
      <ul>
        {failedBatchItems.map(failedItem => (
          <li key={failedItem.id}>
            {pluralize("item", failedItem.value, true)}: {failedItem.label}
          </li>
        ))}
      </ul>
    </WSAlert>
  );

  const renderBatchStates = () => {
    if (selectorIsImportBatchFullyImported({ batch, failedCount })) {
      return (
        <WSList gap="XL">
          <ImportBatchProgress batchId={batchId} />
          {skippedRowsCount > 0 ? (
            <ImportBatchUploadUnuploadedRowsAlert
              batch={batch}
              sheetSlug={CONTRACTORS_SHEET_SLUG}
              entityLabel={CONTRACTOR_ENTITY_LABEL}
            />
          ) : null}
          {renderStats()}
        </WSList>
      );
    }

    if (selectorIsImportBatchPartiallyImported({ batch, failedCount })) {
      return (
        <WSList gap="L">
          <ImportBatchProgress batchId={batchId} />
          {renderErrorsFound()}
          {renderStats()}
        </WSList>
      );
    }

    if (batch.status === BatchStatus.FAILED) {
      return (
        <WSList gap="L">
          <ImportBatchProgress batchId={batchId} />
          {renderErrorsFound()}
          {renderStats(false)}
        </WSList>
      );
    }

    if (isBatchProcessing) {
      return (
        <WSList gap="L">
          <ImportBatchProgress batchId={batchId} />
          {renderStats(false)}
        </WSList>
      );
    }

    if (selectorIsImportBatchUploadFinished({ batch, isEmptyBatch })) {
      return (
        <WSList gap="L">
          <WSFlexBox.CenterY>
            <WSIcon name="details" mr="XS" />
            <WSText.ParagraphSm weight="book" color="gray600">
              A spreadsheet is uploaded and ready to be imported and processed
              as contractors.
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>

          {skippedRowsCount > 0 ? (
            <ImportBatchUploadRejectionsAlert
              batch={batch}
              sheetSlug={CONTRACTORS_SHEET_SLUG}
              entityLabel={CONTRACTOR_ENTITY_LABEL}
            />
          ) : (
            <WSAlert icon="bulb" size="M" theme="info" title="Pro tip">
              View upload to check that all information is accurate prior to
              completing the import process. If there are any changes to make to
              the spreadsheet, delete this import and start a new batch after
              you’ve updated your spreadsheet.
            </WSAlert>
          )}

          {renderStats(false)}
        </WSList>
      );
    }

    if (selectorIsImportBatchUploadInProgress(batch)) {
      // The upload job died mid-flight, the batch will never finish uploading.
      if (isUploadFailed) {
        return <ImportBatchUploadFailedAlert />;
      }

      return (
        <WSList gap="L">
          <ImportBatchUploadProgress batch={batch} />
          <WSFlexBox wrap="nowrap">
            <WSIcon size="S" name="time" mr="XS" />
            <WSText.ParagraphSm weight="book" color="gray600">
              A spreadsheet is being uploaded. If you close this page, the
              progress will continue. Ensure you complete the last step to
              import and process the spreadsheet once it has been uploaded.
            </WSText.ParagraphSm>
          </WSFlexBox>
        </WSList>
      );
    }

    if (isEmptyBatch && selectorIsImportBatchFileUploaded(batch)) {
      return (
        <WSFlexBox wrap="nowrap">
          <WSIcon name="details" mr="XS" />
          <WSText.ParagraphSm weight="book" color="gray600">
            A spreadsheet is selected but not uploaded yet. Click the view
            upload button to upload your formatted spreadsheet.
          </WSText.ParagraphSm>
        </WSFlexBox>
      );
    }

    if (isEmptyBatch && !selectorIsImportBatchFileUploaded(batch)) {
      return (
        <WSEmptyState
          hasBorder={false}
          orientation="horizontal"
          size="M"
          type="documents"
          title="File not uploaded"
          description={
            <WSText.ParagraphSm weight="book" color="gray600" inline>
              Upload your formatted spreadsheet to the batch. Need help setting
              up your spreadsheet?
              <WSButton.Link
                onClick={() => contractorsColumnsGuideModal.open()}
              >
                Learn more here.
              </WSButton.Link>
            </WSText.ParagraphSm>
          }
        />
      );
    }

    return null;
  };

  return (
    <WSCard
      header={{
        label:
          selectorImportBatchUploadedFilename({
            batch,
            entityLabel: CONTRACTOR_ENTITY_LABEL
          }) || "Upload spreadsheet",
        value: { pill }
      }}
      actions={{
        orientation: { xs: "vertical", s: "horizontal" },
        alignment: "fill",
        menuItems,
        buttons
      }}
      divider
    >
      {renderBatchStates()}
    </WSCard>
  );
};
